import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { getNestedValue } from "./utils";
import MultiSelectApiPopup from "./MultiSelectPopup";
import ArrayFieldRenderer from "./ArrayFieldRenderer";

const EMPTY_ARRAY = [];
const CHECKLIST_CORE_FIELDS = new Set(["title", "question", "response"]);

const isTrue = (value) => value === true || value === "true";

const toId = (value) => {
  if (value == null) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

const getBaseField = (field = "") => String(field).split(".")[0];

const getFileName = (url = "") => {
  const clean = String(url).split("?")[0].split("#")[0];
  const lastSegment = clean.split("/").pop() || "file";

  try {
    return decodeURIComponent(lastSegment);
  } catch {
    return lastSegment;
  }
};

const normalizeCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return EMPTY_ARRAY;

  const candidateKeys = [
    "data",
    "results",
    "records",
    "items",
    "projects",
    "users",
    "formats",
  ];

  for (const key of candidateKeys) {
    if (Array.isArray(payload[key])) return payload[key];
  }

  return EMPTY_ARRAY;
};

const extractUploadUrls = (payload) => {
  const data = payload?.data ?? payload;

  if (typeof data === "string") {
    return /^https?:\/\//i.test(data.trim()) ? [data.trim()] : EMPTY_ARRAY;
  }

  if (!data || typeof data !== "object") return EMPTY_ARRAY;

  const directUrls = [data.fileUrl, data.url, data.location].filter(
    (item) => typeof item === "string" && item.trim(),
  );

  const arraySources = [data.urls, data.fileUrls, data.locations, data.files];

  for (const source of arraySources) {
    if (!Array.isArray(source)) continue;

    const normalized = source
      .map((item) => {
        if (typeof item === "string") return item;
        if (!item || typeof item !== "object") return "";
        return item.fileUrl || item.url || item.location || item.path || "";
      })
      .filter(Boolean);

    if (normalized.length) return Array.from(new Set(normalized));
  }

  return Array.from(new Set(directUrls));
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const isValidHttpUrl = (value) => {
  try {
    const parsed = new URL(String(value));
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const getDisplayValue = (value) => {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) return value.map(getDisplayValue).join(", ");

  if (typeof value === "object") {
    return (
      value.roleName ||
      value.name ||
      value.title ||
      value.projectName ||
      JSON.stringify(value)
    );
  }

  return String(value);
};

const getOptionLabel = (option, apiTitle) =>
  option?.[apiTitle] ||
  option?.projectName ||
  option?.name ||
  option?.title ||
  option?.label ||
  "Unnamed";

const normalizeBoolean = (value) => {
  if (typeof value === "string") {
    return ["true", "1", "yes"].includes(value.toLowerCase());
  }

  return Boolean(value);
};

const formatDateInputValue = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const directMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (directMatch) return directMatch[0];
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const parseLatLng = (raw) => {
  if (!raw) return { lat: null, lng: null };

  const match = String(raw).match(/(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)/);

  if (!match) return { lat: null, lng: null };

  return {
    lat: Number.parseFloat(match[1]),
    lng: Number.parseFloat(match[2]),
  };
};

const isValidLatLng = (lat, lng) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180;

const normalizeChecklistItems = (items, subFields = EMPTY_ARRAY) =>
  (Array.isArray(items) ? items : EMPTY_ARRAY).map((item, index) => {
    const text =
      item?.question ??
      item?.CheckPoints ??
      item?.title ??
      `Untitled ${index + 1}`;

    const normalized = {
      title: item?.title ?? text,
      question: item?.question ?? text,
      response: item?.response ?? item?.defaultResponse ?? "N/A",
    };

    subFields.forEach((subField) => {
      if (!CHECKLIST_CORE_FIELDS.has(subField.field)) {
        normalized[subField.field] = item?.[subField.field] ?? "";
      }
    });

    return normalized;
  });

const calculateValue = (operation, operands) => {
  if (!operands.length) return 0;

  switch (operation) {
    case "multiply":
      return operands.reduce((total, number) => total * number, 1);
    case "add":
      return operands.reduce((total, number) => total + number, 0);
    case "subtract":
      return operands
        .slice(1)
        .reduce((total, number) => total - number, operands[0]);
    case "divide":
      return operands
        .slice(1)
        .reduce(
          (total, number) => (number === 0 ? total : total / number),
          operands[0],
        );
    default:
      return 0;
  }
};

const SignatureField = memo(function SignatureField({
  label,
  value,
  onChange,
  uploadApi,
  folder = "signatures",
  uploadField = "image",
}) {
  const signatureRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [broken, setBroken] = useState(false);
  const [error, setError] = useState("");

  const clearPad = useCallback(() => {
    signatureRef.current?.clear();
    setError("");
  }, []);

  const saveSignature = useCallback(async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setError("Please sign before saving.");
      return;
    }

    if (!uploadApi) {
      setError("Signature upload API is not configured.");
      return;
    }

    try {
      setUploading(true);
      setError("");

      const dataUrl = signatureRef.current.toDataURL("image/png");
      const blob = await (await fetch(dataUrl)).blob();
      const fileName = `signature_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}.png`;
      const file = new File([blob], fileName, { type: "image/png" });
      const formData = new FormData();

      formData.append(uploadField, file);
      if (folder) formData.append("folder", folder);

      const response = await axios.post(uploadApi, formData);
      const urls = extractUploadUrls(response);

      if (!urls.length) {
        throw new Error(
          "Upload succeeded, but the server returned no file URL.",
        );
      }

      onChange(urls[0]);
      signatureRef.current.clear();
    } catch (uploadError) {
      setError(getErrorMessage(uploadError, "Signature upload failed."));
    } finally {
      setUploading(false);
    }
  }, [folder, onChange, uploadApi, uploadField]);

  return (
    <div className="form-group">
      <label className="form-label">{label}</label>

      {value ? (
        <div className="image-preview-container" style={{ marginBottom: 12 }}>
          <img
            src={value}
            alt={`${label} preview`}
            className="image-preview"
            style={{
              maxWidth: "100%",
              maxHeight: 150,
              borderRadius: 6,
              objectFit: "contain",
              border: "1px solid #ddd",
              background: "#fff",
            }}
            onError={() => setBroken(true)}
            onLoad={() => setBroken(false)}
          />
          <div style={{ marginTop: 4 }}>
            <a href={value} target="_blank" rel="noopener noreferrer">
              View full signature
            </a>
            {broken && (
              <span style={{ marginLeft: 12, color: "#b00020" }}>
                Image failed to load
              </span>
            )}
          </div>
        </div>
      ) : (
        <p style={{ color: "#666" }}>No signature saved yet.</p>
      )}

      <SignatureCanvas
        ref={signatureRef}
        penColor="black"
        canvasProps={{
          width: 400,
          height: 150,
          className: "signature-canvas",
          style: {
            width: "100%",
            maxWidth: 400,
            border: "1px solid #ccc",
            borderRadius: 6,
            background: "#fff",
          },
        }}
      />

      {error && (
        <div role="alert" style={{ color: "#b00020", marginTop: 8 }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        <button
          type="button"
          className="btn-editForm"
          onClick={clearPad}
          disabled={uploading}
        >
          Clear
        </button>
        <button
          type="button"
          className="btn-editForm btn-editForm-primary"
          onClick={saveSignature}
          disabled={uploading}
        >
          {uploading ? "Saving…" : "Save signature"}
        </button>
      </div>
    </div>
  );
});

const ImageLinkField = memo(function ImageLinkField({
  label,
  value,
  onChange,
  uploadApi,
  folder = "",
  uploadField = "image",
}) {
  const [urlInput, setUrlInput] = useState(value || "");
  const [showPreview, setShowPreview] = useState(false);
  const [broken, setBroken] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setUrlInput(value || "");
    setBroken(false);
  }, [value]);

  const applyUrl = useCallback(() => {
    const normalizedUrl = String(urlInput || "").trim();

    if (!normalizedUrl) {
      onChange("");
      setError("");
      return;
    }

    if (!isValidHttpUrl(normalizedUrl)) {
      setError("Enter a valid http or https image URL.");
      return;
    }

    setError("");
    setBroken(false);
    onChange(normalizedUrl);
  }, [onChange, urlInput]);

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;

      if (!uploadApi) {
        setError("Image upload API is not configured.");
        return;
      }

      try {
        setUploading(true);
        setError("");

        const formData = new FormData();
        formData.append(uploadField, file);
        if (folder) formData.append("folder", folder);

        const response = await axios.post(uploadApi, formData);
        const urls = extractUploadUrls(response);

        if (!urls.length) {
          throw new Error(
            "Upload succeeded, but the server returned no image URL.",
          );
        }

        onChange(urls[0]);
        setUrlInput(urls[0]);
        setBroken(false);
      } catch (uploadError) {
        setError(getErrorMessage(uploadError, "Image upload failed."));
      } finally {
        setUploading(false);
      }
    },
    [folder, onChange, uploadApi, uploadField],
  );

  return (
    <div className="form-group">
      <label className="form-label">{label}</label>

      {value ? (
        <>
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            style={{
              display: "block",
              width: "100%",
              padding: 0,
              border: 0,
              background: "transparent",
              cursor: "zoom-in",
            }}
            aria-label={`Open ${label} preview`}
          >
            <img
              src={value}
              alt={label}
              className="image-preview"
              style={{
                maxHeight: 256,
                width: "100%",
                objectFit: "contain",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: "#f9fafb",
              }}
              onError={() => setBroken(true)}
              onLoad={() => setBroken(false)}
            />
          </button>

          {broken && (
            <div role="alert" style={{ color: "#b00020", marginTop: 8 }}>
              Image failed to load.
            </div>
          )}

          {showPreview && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label={`${label} full preview`}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
              onClick={() => setShowPreview(false)}
            >
              <img
                src={value}
                alt={label}
                className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
                onClick={(event) => event.stopPropagation()}
              />
            </div>
          )}
        </>
      ) : (
        <p style={{ color: "#666" }}>No image available.</p>
      )}

      <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            type="url"
            value={urlInput}
            placeholder="Paste image URL"
            className="form-input"
            style={{ flex: "1 1 280px" }}
            onChange={(event) => setUrlInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                applyUrl();
              }
            }}
          />
          <button
            type="button"
            className="btn-editForm"
            onClick={applyUrl}
            disabled={uploading}
          >
            Apply URL
          </button>
        </div>

        <input
          type="file"
          accept="image/*"
          className="form-input"
          onChange={(event) => {
            handleFile(event.target.files?.[0]);
            event.target.value = "";
          }}
          disabled={uploading || !uploadApi}
        />
      </div>

      {error && (
        <div role="alert" style={{ color: "#b00020", marginTop: 8 }}>
          {error}
        </div>
      )}

      {uploading && <div style={{ marginTop: 8 }}>Uploading…</div>}
    </div>
  );
});

const GeolocationField = memo(function GeolocationField({
  label,
  value,
  onChange,
  zoom = 15,
}) {
  const initial = useMemo(() => parseLatLng(value), [value]);
  const [lat, setLat] = useState(
    Number.isFinite(initial.lat) ? String(initial.lat) : "",
  );
  const [lng, setLng] = useState(
    Number.isFinite(initial.lng) ? String(initial.lng) : "",
  );
  const [error, setError] = useState("");
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    setLat(Number.isFinite(initial.lat) ? String(initial.lat) : "");
    setLng(Number.isFinite(initial.lng) ? String(initial.lng) : "");
    setError("");
  }, [initial.lat, initial.lng]);

  const apply = useCallback(() => {
    const numericLat = Number.parseFloat(lat);
    const numericLng = Number.parseFloat(lng);

    if (!isValidLatLng(numericLat, numericLng)) {
      setError(
        "Enter a valid latitude (-90 to 90) and longitude (-180 to 180).",
      );
      return;
    }

    setError("");
    onChange(`(${numericLat}, ${numericLng})`);
  }, [lat, lng, onChange]);

  const useDeviceLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by this browser.");
      return;
    }

    setLocating(true);
    setError("");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const numericLat = Number(position.coords.latitude.toFixed(6));
        const numericLng = Number(position.coords.longitude.toFixed(6));

        setLat(String(numericLat));
        setLng(String(numericLng));
        onChange(`(${numericLat}, ${numericLng})`);
        setLocating(false);
      },
      (locationError) => {
        setError(locationError.message || "Unable to get device location.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }, [onChange]);

  const numericLat = Number.parseFloat(lat);
  const numericLng = Number.parseFloat(lng);
  const showMap = isValidLatLng(numericLat, numericLng);

  return (
    <div className="form-group">
      <label className="form-label">{label}</label>

      <div
        style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}
      >
        <input
          type="number"
          step="0.000001"
          placeholder="Latitude"
          className="form-input"
          value={lat}
          onChange={(event) => setLat(event.target.value)}
          style={{ minWidth: 140 }}
        />
        <input
          type="number"
          step="0.000001"
          placeholder="Longitude"
          className="form-input"
          value={lng}
          onChange={(event) => setLng(event.target.value)}
          style={{ minWidth: 160 }}
        />
        <button
          type="button"
          className="btn-editForm btn-editForm-primary"
          onClick={apply}
        >
          Apply
        </button>
        <button
          type="button"
          className="btn-editForm"
          onClick={useDeviceLocation}
          disabled={locating}
        >
          {locating ? "Locating…" : "Use current location"}
        </button>
        <button
          type="button"
          className="btn-editForm"
          onClick={() => {
            setLat("");
            setLng("");
            setError("");
            onChange("");
          }}
        >
          Clear
        </button>
      </div>

      {error && (
        <div role="alert" style={{ color: "#b00020", marginBottom: 8 }}>
          {error}
        </div>
      )}

      {showMap ? (
        <>
          <div style={{ height: 300, borderRadius: 10, overflow: "hidden" }}>
            <iframe
              title={`${label} map`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              src={`https://www.google.com/maps?q=${numericLat},${numericLng}&z=${zoom}&output=embed`}
            />
          </div>
          <div style={{ marginTop: 8 }}>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${numericLat},${numericLng}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Google Maps
            </a>
          </div>
        </>
      ) : (
        <p style={{ color: "#666" }}>No valid location selected.</p>
      )}
    </div>
  );
});

function areSafetyChecklistPropsEqual(previous, next) {
  const previousDependencyField =
    previous.col.fieldDependenton?.split(".")[0] || "permitTypes";
  const nextDependencyField =
    next.col.fieldDependenton?.split(".")[0] || "permitTypes";

  return (
    previous.baseField === next.baseField &&
    previous.col.api === next.col.api &&
    previous.col.label === next.col.label &&
    previous.col.fieldDependenton === next.col.fieldDependenton &&
    JSON.stringify(previous.col.subFields || EMPTY_ARRAY) ===
      JSON.stringify(next.col.subFields || EMPTY_ARRAY) &&
    JSON.stringify(previous.formData?.[previousDependencyField]) ===
      JSON.stringify(next.formData?.[nextDependencyField]) &&
    JSON.stringify(previous.formData?.[previous.baseField]) ===
      JSON.stringify(next.formData?.[next.baseField]) &&
    JSON.stringify(previous.dropdownOptions?.[previous.col.api]) ===
      JSON.stringify(next.dropdownOptions?.[next.col.api])
  );
}

const SafetyChecklistWithPermit = memo(function SafetyChecklistWithPermit({
  col,
  formData,
  onChange,
  baseField,
  dropdownOptions,
}) {
  const subFields = Array.isArray(col.subFields) ? col.subFields : EMPTY_ARRAY;
  const initialItems = normalizeChecklistItems(
    formData?.[baseField],
    subFields,
  );
  const [items, setItems] = useState(initialItems);
  const [list, setList] = useState(EMPTY_ARRAY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onChangeRef = useRef(onChange);
  const lastPushedRef = useRef(JSON.stringify(initialItems));
  const lastSelectedKeyRef = useRef(null);
  const externalItemsLoadedRef = useRef(initialItems.length > 0);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const dependencyField = col.fieldDependenton?.split(".")[0] || "permitTypes";
  const dependencyValue = formData?.[dependencyField];

  const selectedKey = useMemo(() => {
    const selectedId = toId(dependencyValue);

    if (selectedId && /^[a-f\d]{24}$/i.test(selectedId)) {
      return `id:${selectedId}`;
    }

    const selectedName =
      formData?.permitTypes?.permitsType ||
      formData?.checklistName ||
      formData?.name ||
      (typeof dependencyValue === "string" ? dependencyValue : "");

    return selectedName ? `name:${String(selectedName).trim()}` : null;
  }, [dependencyValue, formData]);

  const cachedOptions = dropdownOptions?.[col.api];

  useEffect(() => {
    if (Array.isArray(cachedOptions) && cachedOptions.length) {
      setList(cachedOptions);
      setError("");
      return undefined;
    }

    if (!col.api) {
      setList(EMPTY_ARRAY);
      return undefined;
    }

    const controller = new AbortController();

    const loadOptions = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await axios.get(col.api, {
          signal: controller.signal,
        });

        setList(normalizeCollection(response.data));
      } catch (requestError) {
        if (requestError?.code !== "ERR_CANCELED") {
          setError(
            getErrorMessage(requestError, "Failed to load checklist data."),
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadOptions();
    return () => controller.abort();
  }, [cachedOptions, col.api]);

  const externalItems = useMemo(
    () => normalizeChecklistItems(formData?.[baseField], subFields),
    [baseField, formData, subFields],
  );
  const externalItemsJson = JSON.stringify(externalItems);

  useEffect(() => {
    if (!externalItems.length) return;

    if (
      externalItemsJson !== lastPushedRef.current &&
      externalItemsJson !== JSON.stringify(items)
    ) {
      externalItemsLoadedRef.current = true;
      lastPushedRef.current = externalItemsJson;
      setItems(externalItems);
    }
  }, [externalItems, externalItemsJson, items]);

  useEffect(() => {
    if (externalItemsLoadedRef.current || !selectedKey) return;
    if (lastSelectedKeyRef.current === selectedKey) return;

    const separatorIndex = selectedKey.indexOf(":");
    const mode = selectedKey.slice(0, separatorIndex);
    const target = selectedKey.slice(separatorIndex + 1);

    const match = list.find((option) => {
      if (mode === "id") return toId(option) === target;

      const optionName =
        option?.permitsType || option?.checklistName || option?.name || "";
      return String(optionName).trim() === target;
    });

    if (!match) return;

    const sourceItems = Array.isArray(match.SafetyChecks)
      ? match.SafetyChecks
      : Array.isArray(match.items)
        ? match.items
        : EMPTY_ARRAY;

    const normalized = normalizeChecklistItems(sourceItems, subFields);
    lastSelectedKeyRef.current = selectedKey;
    setItems(normalized);
  }, [list, selectedKey, subFields]);

  useEffect(() => {
    const normalized = normalizeChecklistItems(items, subFields);
    const normalizedJson = JSON.stringify(normalized);

    if (normalizedJson === lastPushedRef.current) return;

    lastPushedRef.current = normalizedJson;
    onChangeRef.current(baseField, normalized);
  }, [baseField, items, subFields]);

  const handleChange = useCallback((index, field, nextValue) => {
    setItems((currentItems) =>
      currentItems.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: nextValue } : item,
      ),
    );
  }, []);

  if (loading) return <p>Loading checklist…</p>;
  if (error) return <p style={{ color: "#b00020" }}>{error}</p>;
  if (!selectedKey && !items.length) {
    return (
      <p style={{ color: "#666" }}>Select a type to load the checklist.</p>
    );
  }

  return (
    <div className="form-group">
      <label className="form-label">{col.label}</label>

      <div style={{ overflowX: "auto" }}>
        <table
          className="table"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              {subFields.map((subField) => (
                <th
                  key={subField.field}
                  style={{ textAlign: "left", padding: 8 }}
                >
                  {subField.label || subField.field}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`${item.question}-${index}`}>
                {subFields.map((subField) => {
                  const currentValue = item[subField.field] ?? "";

                  if (
                    subField.type === "dropdown" &&
                    Array.isArray(subField.options)
                  ) {
                    return (
                      <td key={subField.field} style={{ padding: 8 }}>
                        <select
                          value={currentValue}
                          onChange={(event) =>
                            handleChange(
                              index,
                              subField.field,
                              event.target.value,
                            )
                          }
                          className="form-input"
                        >
                          {subField.options.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  }

                  if (subField.edit === false || subField.edit === "false") {
                    return (
                      <td key={subField.field}>
                        {getDisplayValue(currentValue)}
                      </td>
                    );
                  }

                  return (
                    <td key={subField.field} style={{ padding: 8 }}>
                      <input
                        type={subField.type || "text"}
                        value={currentValue}
                        onChange={(event) =>
                          handleChange(
                            index,
                            subField.field,
                            event.target.value,
                          )
                        }
                        className="form-input"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}

            {!items.length && (
              <tr>
                <td
                  colSpan={Math.max(subFields.length, 1)}
                  style={{ padding: 8 }}
                >
                  No checklist items found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}, areSafetyChecklistPropsEqual);

const WorkpermitAdditionField = memo(function WorkpermitAdditionField({
  col,
  formData,
  onChange,
  dropdownOptions,
}) {
  const currentValue = Array.isArray(getNestedValue(formData, col.field))
    ? getNestedValue(formData, col.field)
    : EMPTY_ARRAY;

  const permitField = col.subFields?.[0] || {};
  const pageFieldsConfig = col.subFields?.[1] || {};
  const permitApi = permitField.api || "";
  const permitApiTitle = permitField.apiTitle || "name";
  const fieldTypeOptions =
    pageFieldsConfig.subFields?.[2]?.options || EMPTY_ARRAY;

  const cachedPermitOptions =
    dropdownOptions?.[permitField.field] ||
    dropdownOptions?.[permitApi] ||
    EMPTY_ARRAY;

  const [permitOptions, setPermitOptions] = useState(
    normalizeCollection(cachedPermitOptions),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const normalizedCachedOptions = normalizeCollection(cachedPermitOptions);

    if (normalizedCachedOptions.length) {
      setPermitOptions(normalizedCachedOptions);
      setError("");
      return undefined;
    }

    if (!permitApi) return undefined;

    const controller = new AbortController();

    const loadPermitOptions = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await axios.get(permitApi, {
          signal: controller.signal,
        });
        setPermitOptions(normalizeCollection(response.data));
      } catch (requestError) {
        if (requestError?.code !== "ERR_CANCELED") {
          setError(
            getErrorMessage(requestError, "Failed to load permit types."),
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadPermitOptions();
    return () => controller.abort();
  }, [cachedPermitOptions, permitApi]);

  const updatePermit = useCallback(
    (permitIndex, updater) => {
      const nextValue = currentValue.map((permit, index) =>
        index === permitIndex ? updater(permit) : permit,
      );
      onChange(col.field, nextValue);
    },
    [col.field, currentValue, onChange],
  );

  const addPermit = useCallback(() => {
    onChange(col.field, [
      ...currentValue,
      { permitType: "", PageFields: EMPTY_ARRAY },
    ]);
  }, [col.field, currentValue, onChange]);

  const removePermit = useCallback(
    (permitIndex) => {
      onChange(
        col.field,
        currentValue.filter((_, index) => index !== permitIndex),
      );
    },
    [col.field, currentValue, onChange],
  );

  return (
    <div className="form-group">
      <label className="form-label">{col.label}</label>

      {error && (
        <div style={{ color: "#b00020", marginBottom: 8 }}>{error}</div>
      )}

      {currentValue.map((permit, permitIndex) => {
        const pageFields = Array.isArray(permit.PageFields)
          ? permit.PageFields
          : EMPTY_ARRAY;

        return (
          <div
            key={permit._id || `permit-${permitIndex}`}
            style={{
              border: "1px solid #ddd",
              borderRadius: 8,
              padding: 12,
              marginBottom: 12,
              background: "#fafafa",
            }}
          >
            <div style={{ marginBottom: 12 }}>
              <label
                style={{ fontWeight: 600, display: "block", marginBottom: 6 }}
              >
                Permit Type
              </label>
              <select
                className="form-input"
                value={toId(permit.permitType)}
                onChange={(event) =>
                  updatePermit(permitIndex, (currentPermit) => ({
                    ...currentPermit,
                    permitType: event.target.value,
                  }))
                }
                disabled={loading}
              >
                <option value="">-- Select Permit Type --</option>
                {permitOptions.map((option) => (
                  <option key={toId(option)} value={toId(option)}>
                    {getOptionLabel(option, permitApiTitle)}
                  </option>
                ))}
              </select>
              {loading && (
                <div style={{ marginTop: 4 }}>Loading permit types…</div>
              )}
            </div>

            <div>
              <label
                style={{ fontWeight: 600, display: "block", marginBottom: 6 }}
              >
                Page Fields
              </label>

              {pageFields.map((pageField, pageFieldIndex) => (
                <div
                  key={pageField._id || `page-field-${pageFieldIndex}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(140px, 1fr)) auto",
                    gap: 8,
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <input
                    type="text"
                    placeholder="Title"
                    className="form-input"
                    value={pageField.Title || ""}
                    onChange={(event) =>
                      updatePermit(permitIndex, (currentPermit) => ({
                        ...currentPermit,
                        PageFields: pageFields.map((item, index) =>
                          index === pageFieldIndex
                            ? { ...item, Title: event.target.value }
                            : item,
                        ),
                      }))
                    }
                  />

                  <input
                    type="text"
                    placeholder="Header Key"
                    className="form-input"
                    value={pageField.Headers || ""}
                    onChange={(event) =>
                      updatePermit(permitIndex, (currentPermit) => ({
                        ...currentPermit,
                        PageFields: pageFields.map((item, index) =>
                          index === pageFieldIndex
                            ? { ...item, Headers: event.target.value }
                            : item,
                        ),
                      }))
                    }
                  />

                  <select
                    className="form-input"
                    value={pageField.Type || ""}
                    onChange={(event) =>
                      updatePermit(permitIndex, (currentPermit) => ({
                        ...currentPermit,
                        PageFields: pageFields.map((item, index) =>
                          index === pageFieldIndex
                            ? { ...item, Type: event.target.value }
                            : item,
                        ),
                      }))
                    }
                  >
                    <option value="">Select Type</option>
                    {fieldTypeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  <input
                    type="text"
                    placeholder="Options (comma separated)"
                    className="form-input"
                    value={(pageField.Options || EMPTY_ARRAY).join(", ")}
                    onChange={(event) => {
                      const options = event.target.value
                        .split(",")
                        .map((item) => item.trim())
                        .filter(Boolean);

                      updatePermit(permitIndex, (currentPermit) => ({
                        ...currentPermit,
                        PageFields: pageFields.map((item, index) =>
                          index === pageFieldIndex
                            ? { ...item, Options: options }
                            : item,
                        ),
                      }));
                    }}
                  />

                  <button
                    type="button"
                    className="btn-editForm"
                    onClick={() =>
                      updatePermit(permitIndex, (currentPermit) => ({
                        ...currentPermit,
                        PageFields: pageFields.filter(
                          (_, index) => index !== pageFieldIndex,
                        ),
                      }))
                    }
                    title="Remove field"
                    aria-label="Remove page field"
                  >
                    ✕
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="btn-editForm btn-editForm-primary"
                onClick={() =>
                  updatePermit(permitIndex, (currentPermit) => ({
                    ...currentPermit,
                    PageFields: [
                      ...pageFields,
                      {
                        Title: "",
                        Headers: "",
                        Type: "",
                        Options: EMPTY_ARRAY,
                      },
                    ],
                  }))
                }
              >
                + Add Page Field
              </button>
            </div>

            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn-editForm"
                onClick={() => removePermit(permitIndex)}
              >
                Remove Permit Type
              </button>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="btn-editForm btn-editForm-primary"
        onClick={addPermit}
      >
        + Add Permit Type
      </button>
    </div>
  );
});

const FileField = memo(function FileField({ col, value, onChange }) {
  const baseField = getBaseField(col.field);
  const isArrayField = isTrue(col.array);
  const existing = useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : EMPTY_ARRAY),
    [value],
  );

  const [manualLink, setManualLink] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const updateValue = useCallback(
    (urls) => {
      if (isArrayField) {
        const nextUrls = Array.from(
          new Set(
            [...existing, ...urls].filter(
              (item) => typeof item === "string" && item.trim(),
            ),
          ),
        );
        onChange(baseField, nextUrls);
      } else {
        onChange(baseField, urls[0] || "");
      }
    },
    [baseField, existing, isArrayField, onChange],
  );

  const uploadFiles = useCallback(
    async (files) => {
      if (!col.uploadApi) {
        throw new Error("File upload API is not configured.");
      }

      const formData = new FormData();
      const uploadField = col.uploadField || "file";
      files.forEach((file) => formData.append(uploadField, file));

      const response = await axios.post(col.uploadApi, formData);
      const urls = extractUploadUrls(response);

      if (!urls.length) {
        throw new Error(
          "Upload succeeded, but the server returned no file URL.",
        );
      }

      return urls;
    },
    [col.uploadApi, col.uploadField],
  );

  const handleFilePick = useCallback(
    async (event) => {
      const input = event.target;
      const pickedFiles = Array.from(input.files || EMPTY_ARRAY);

      if (!pickedFiles.length) return;

      if (!isArrayField && pickedFiles.length > 1) {
        setError("Only one file is allowed for this field.");
        input.value = "";
        return;
      }

      try {
        setUploading(true);
        setError("");
        const urls = await uploadFiles(pickedFiles);
        updateValue(urls);
      } catch (uploadError) {
        setError(getErrorMessage(uploadError, "File upload failed."));
      } finally {
        setUploading(false);
        input.value = "";
      }
    },
    [isArrayField, updateValue, uploadFiles],
  );

  const removeItem = useCallback(
    (index) => {
      if (isArrayField) {
        onChange(
          baseField,
          existing.filter((_, itemIndex) => itemIndex !== index),
        );
      } else {
        onChange(baseField, "");
      }
    },
    [baseField, existing, isArrayField, onChange],
  );

  const addManualLink = useCallback(() => {
    const normalizedUrl = manualLink.trim();

    if (!normalizedUrl) {
      setError("Enter a file URL.");
      return;
    }

    if (!isValidHttpUrl(normalizedUrl)) {
      setError("Enter a valid http or https URL.");
      return;
    }

    if (!isArrayField && existing.length) {
      setError("Remove the current file before adding another one.");
      return;
    }

    setError("");
    updateValue([normalizedUrl]);
    setManualLink("");
  }, [existing.length, isArrayField, manualLink, updateValue]);

  return (
    <div className="form-group">
      <label className="form-label">{col.label}</label>

      {existing.length > 0 && (
        <ul className="file-list">
          {existing.map((item, index) => (
            <li key={`${item}-${index}`}>
              <a
                className="file-item"
                href={item}
                target="_blank"
                rel="noopener noreferrer"
                title={getFileName(item)}
              >
                <span className="file-icon" aria-hidden="true">
                  📎
                </span>
                <span className="file-name">{getFileName(item)}</span>
              </a>
              <button
                type="button"
                className="file-remove"
                onClick={() => removeItem(index)}
                title="Remove"
                aria-label={`Remove ${getFileName(item)}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        type="file"
        className="form-input"
        multiple={isArrayField}
        onChange={handleFilePick}
        accept={col.accept || "*/*"}
        disabled={uploading || !col.uploadApi}
      />

      <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          type="url"
          value={manualLink}
          placeholder="Paste file link (https://...)"
          className="form-input"
          style={{ flex: "1 1 280px" }}
          onChange={(event) => setManualLink(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addManualLink();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-sm btn-outline"
          onClick={addManualLink}
          disabled={uploading}
        >
          Add
        </button>
      </div>

      {uploading && <div style={{ marginTop: 8 }}>Uploading…</div>}
      {error && (
        <div role="alert" style={{ marginTop: 8, color: "#b00020" }}>
          {error}
        </div>
      )}
    </div>
  );
});

const FieldRenderer = ({
  col,
  formData = {},
  dropdownOptions = {},
  decryptedValue,
  onChange,
  setShowPasswordChange,
  handleDecrypt,
  isCreate,
}) => {
  const field = String(col.field || "");
  const baseField = getBaseField(field);
  const value = getNestedValue(formData, field);
  const [showPopup, setShowPopup] = useState(false);

  const calculationFields = useMemo(
    () =>
      Object.keys(col)
        .filter((key) => /^\d+$/.test(key))
        .sort((a, b) => Number(a) - Number(b))
        .map((key) => col[key])
        .filter(Boolean),
    [col],
  );

  const calculationOperands = calculationFields.map(
    (operandField) =>
      Number.parseFloat(getNestedValue(formData, operandField)) || 0,
  );
  const calculationSignature = JSON.stringify(calculationOperands);

  const calculatedValue = useMemo(
    () => calculateValue(col.calculate, calculationOperands),
    // calculationSignature is the stable primitive representation of the operands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [col.calculate, calculationSignature],
  );

  useEffect(() => {
    if (!col.calculate) return;

    const currentNumber = Number(value);
    if (!Number.isFinite(currentNumber) || currentNumber !== calculatedValue) {
      onChange(baseField, calculatedValue);
    }
  }, [baseField, calculatedValue, col.calculate, onChange, value]);

  if (col.type === "boolean") {
    return (
      <div className="form-group boolean-field">
        <label className="form-label">{col.label}</label>
        <label className="form-toggle">
          <input
            type="checkbox"
            checked={normalizeBoolean(value)}
            onChange={(event) => onChange(baseField, event.target.checked)}
            disabled={col.disabled === true}
          />
        </label>
      </div>
    );
  }

  if (col.type === "password") {
    const passwordFieldName = baseField || "password";

    if (isCreate) {
      return (
        <div className="form-group">
          <label className="form-label" htmlFor={`${passwordFieldName}-input`}>
            {col.label}
          </label>
          <input
            id={`${passwordFieldName}-input`}
            type="password"
            className="form-input"
            value={String(formData?.[passwordFieldName] || "")}
            onChange={(event) =>
              onChange(passwordFieldName, event.target.value)
            }
            placeholder={col.placeholder || "Enter password"}
            autoComplete="new-password"
            minLength={col.minLength || 4}
          />
          <small className="form-field-help">
            Password must contain at least {col.minLength || 4} characters.
          </small>
        </div>
      );
    }

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <button
          type="button"
          className="btn-editForm-password-change"
          onClick={() => setShowPasswordChange?.(true)}
        >
          Change Password
        </button>
      </div>
    );
  }

  if (col.type === "plainPassword") {
    if (isCreate) return null;

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <input
          className="form-input"
          value={decryptedValue || "••••••••"}
          readOnly
        />
        <button
          type="button"
          className="btn-editForm-password-change"
          onClick={handleDecrypt}
        >
          Decrypt
        </button>
      </div>
    );
  }

  if (col.type === "dropdown") {
    const selected = getNestedValue(formData, baseField);
    const rawOptions =
      dropdownOptions?.[field] ??
      dropdownOptions?.[baseField] ??
      dropdownOptions?.[col.api] ??
      EMPTY_ARRAY;
    const apiOptions = normalizeCollection(rawOptions);
    const staticOptions = Array.isArray(col.options)
      ? col.options
      : EMPTY_ARRAY;
    const selectedId = toId(selected);
    const selectedOption =
      apiOptions.find((option) => toId(option) === selectedId) ||
      (selected && typeof selected === "object" ? selected : null);

    const staticSelected = staticOptions.find((option) => {
      const optionValue =
        option && typeof option === "object" ? option.value : option;
      return String(optionValue) === String(selected ?? "");
    });

    const selectedLabel = col.apiTitle
      ? getOptionLabel(selectedOption, col.apiTitle)
      : staticSelected && typeof staticSelected === "object"
        ? staticSelected.label || staticSelected.value
        : staticSelected || "No selection";

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>

        <div className="chip-container">
          <span className="form-chip">
            {selectedId ? selectedLabel : "No selection"}
          </span>
        </div>

        <select
          className="form-input"
          value={selectedId}
          onChange={(event) => onChange(baseField, event.target.value)}
          required={col.required === true}
          disabled={col.disabled === true}
        >
          <option value="">-- Select {col.label} --</option>

          {col.apiTitle
            ? apiOptions.map((option) => (
                <option key={toId(option)} value={toId(option)}>
                  {getOptionLabel(option, col.apiTitle)}
                </option>
              ))
            : staticOptions.map((option, index) => {
                const optionValue =
                  option && typeof option === "object" ? option.value : option;
                const optionLabel =
                  option && typeof option === "object"
                    ? option.label || option.value
                    : option;

                return (
                  <option key={`${optionValue}-${index}`} value={optionValue}>
                    {optionLabel}
                  </option>
                );
              })}
        </select>

        {isTrue(col.details) && selectedOption && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-white shadow-sm">
            <ul className="divide-y divide-gray-200">
              {(col.BoxShow || EMPTY_ARRAY).map((fieldKey) => (
                <li
                  key={fieldKey}
                  className="px-4 py-3 text-sm sm:flex sm:items-center sm:justify-between"
                >
                  <div className="font-medium text-gray-600 sm:w-1/3">
                    {fieldKey}
                  </div>
                  <div className="mt-1 text-gray-900 sm:mt-0 sm:w-2/3 break-words">
                    {getDisplayValue(getNestedValue(selectedOption, fieldKey))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  if (col.type === "checkbox" && isTrue(col.array) && col.api) {
    const existingValues = Array.isArray(formData?.[baseField])
      ? formData[baseField]
      : EMPTY_ARRAY;
    const selectedValues = existingValues.map(toId).filter(Boolean);
    const rawOptions =
      dropdownOptions?.[field] ??
      dropdownOptions?.[baseField] ??
      dropdownOptions?.[col.api] ??
      EMPTY_ARRAY;
    const apiOptions = normalizeCollection(rawOptions);

    const findOption = (id) =>
      apiOptions.find((option) => toId(option) === String(id)) ||
      existingValues.find(
        (item) => typeof item === "object" && toId(item) === String(id),
      );

    const handleLiveChange = (nextValues) => {
      onChange(
        baseField,
        (Array.isArray(nextValues) ? nextValues : EMPTY_ARRAY)
          .map(toId)
          .filter(Boolean),
      );
    };

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>

        <div className="chip-container">
          {selectedValues.length ? (
            selectedValues.map((id) => {
              const option = findOption(id);
              const label = option
                ? getOptionLabel(option, col.apiTitle)
                : `(${id.slice(0, 6)}...)`;

              return (
                <span key={id} className="form-chip">
                  {label}
                  <button
                    type="button"
                    className="chip-remove"
                    title={`Remove ${label}`}
                    onClick={() =>
                      onChange(
                        baseField,
                        selectedValues.filter(
                          (selectedId) => selectedId !== id,
                        ),
                      )
                    }
                  >
                    ×
                  </button>
                </span>
              );
            })
          ) : (
            <span className="form-chip empty">No selections</span>
          )}
        </div>

        <button
          type="button"
          className="btn-editForm-popup-open"
          onClick={() => setShowPopup(true)}
        >
          + Select {col.label}
        </button>

        {showPopup && (
          <MultiSelectApiPopup
            api={col.api}
            apiTitle={col.apiTitle}
            title={`Select ${col.label}`}
            searchPlaceholder={`Search ${String(col.label).toLowerCase()}...`}
            options={apiOptions}
            selectedValues={selectedValues}
            onClose={() => setShowPopup(false)}
            onChangeLive={handleLiveChange}
          />
        )}

        {isTrue(col.details) && selectedValues.length > 0 && (
          <div className="mt-4 space-y-3">
            {selectedValues.map((id) => {
              const selectedOption = findOption(id);
              if (!selectedOption) return null;

              return (
                <details
                  key={id}
                  className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  <summary className="cursor-pointer select-none px-4 py-2 text-sm font-semibold text-gray-800 bg-gray-50">
                    {getOptionLabel(selectedOption, col.apiTitle)}
                  </summary>
                  <div className="border-t border-gray-100">
                    <ul className="divide-y divide-gray-200">
                      {(col.BoxShow || EMPTY_ARRAY).map((fieldKey) => (
                        <li
                          key={fieldKey}
                          className="px-4 py-3 text-sm sm:flex sm:items-center sm:justify-between"
                        >
                          <div className="font-medium text-gray-600 sm:w-1/3">
                            {fieldKey}
                          </div>
                          <div className="mt-1 text-gray-900 sm:mt-0 sm:w-2/3 break-words">
                            {getDisplayValue(
                              getNestedValue(selectedOption, fieldKey),
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (
    col.type === "checkboxPrefilled" &&
    isTrue(col.array) &&
    Array.isArray(col.checkBox)
  ) {
    const selectedItems = Array.isArray(formData?.[baseField])
      ? formData[baseField]
      : EMPTY_ARRAY;
    const allSelected =
      col.checkBox.length > 0 && selectedItems.length === col.checkBox.length;

    const toggle = (item) => {
      onChange(
        baseField,
        selectedItems.includes(item)
          ? selectedItems.filter((selectedItem) => selectedItem !== item)
          : [...selectedItems, item],
      );
    };

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>

        <label className="checkbox-prefilled-item font-semibold mb-1">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() =>
              onChange(baseField, allSelected ? EMPTY_ARRAY : [...col.checkBox])
            }
          />
          Select All
        </label>

        <div className="checkbox-prefilled-group">
          {col.checkBox.map((item) => (
            <label key={item} className="checkbox-prefilled-item">
              <input
                type="checkbox"
                checked={selectedItems.includes(item)}
                onChange={() => toggle(item)}
              />
              {item}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (
    (col.type === "permitchecklists" || col.type === "safetychecks") &&
    col.api
  ) {
    return (
      <SafetyChecklistWithPermit
        col={col}
        formData={formData}
        onChange={onChange}
        baseField={baseField}
        dropdownOptions={dropdownOptions}
      />
    );
  }

  if (
    (col.type === "subString" || col.type === "permitchecklists") &&
    Array.isArray(col.subFields)
  ) {
    return (
      <ArrayFieldRenderer col={col} formData={formData} onChange={onChange} />
    );
  }

  if (col.type === "imageLink") {
    return (
      <ImageLinkField
        label={col.label}
        value={value || ""}
        onChange={(nextUrl) => onChange(baseField, nextUrl)}
        uploadApi={col.uploadApi}
        folder={col.folder || ""}
        uploadField={col.uploadField || "image"}
      />
    );
  }

  if (col.type === "file") {
    return <FileField col={col} value={value} onChange={onChange} />;
  }

  if (col.type === "Date" || col.type === "date") {
    return (
      <div className="form-group">
        <label className="form-label" htmlFor={field}>
          {col.label}
        </label>
        <input
          id={field}
          name={field}
          type="date"
          className="form-input"
          value={formatDateInputValue(value)}
          onChange={(event) => onChange(baseField, event.target.value)}
          min={col.min}
          max={col.max}
        />
      </div>
    );
  }

  if (col.type === "geolocation") {
    return (
      <GeolocationField
        label={col.label}
        value={value || ""}
        onChange={(nextValue) => onChange(baseField, nextValue)}
        zoom={col.zoom || 15}
      />
    );
  }

  if (col.type === "Time" || col.type === "time12") {
    const timeValue = String(value ?? "");
    const match = timeValue.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    const hour = match ? match[1] : "8";
    const minute = match ? match[2] : "00";
    const period = match ? match[3].toUpperCase() : "AM";

    const normalizeHour = (nextHour) => {
      const numericHour = Number.parseInt(nextHour, 10);
      return Number.isNaN(numericHour)
        ? "12"
        : String(((numericHour - 1 + 12) % 12) + 1);
    };

    const updateTime = (nextHour, nextMinute, nextPeriod) => {
      const normalizedMinute = String(nextMinute).padStart(2, "0");
      const normalizedPeriod = nextPeriod === "PM" ? "PM" : "AM";
      onChange(
        baseField,
        `${normalizeHour(nextHour)}:${normalizedMinute} ${normalizedPeriod}`,
      );
    };

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <div style={{ display: "flex", gap: 10 }}>
          <select
            className="form-input"
            value={normalizeHour(hour)}
            onChange={(event) => updateTime(event.target.value, minute, period)}
          >
            {Array.from({ length: 12 }, (_, index) => String(index + 1)).map(
              (option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ),
            )}
          </select>

          <select
            className="form-input"
            value={String(minute).padStart(2, "0")}
            onChange={(event) => updateTime(hour, event.target.value, period)}
          >
            {(col.minuteOptions || ["00", "15", "30", "45"]).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          <select
            className="form-input"
            value={period}
            onChange={(event) => updateTime(hour, minute, event.target.value)}
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      </div>
    );
  }

  if (col.type === "signature") {
    return (
      <SignatureField
        label={col.label}
        value={value || ""}
        onChange={(nextUrl) => onChange(baseField, nextUrl)}
        uploadApi={col.uploadApi}
        folder={col.folder || "signatures"}
        uploadField={col.uploadField || "image"}
      />
    );
  }

  if (col.calculate) {
    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <input
          type="number"
          className="form-input"
          value={calculatedValue}
          readOnly
        />
      </div>
    );
  }

  if (col.edit === false || col.edit === "false") {
    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <div className="form-readonly">{getDisplayValue(value)}</div>
      </div>
    );
  }

  if (col.type === "Number" || col.type === "number") {
    const minValue = col.min == null ? undefined : Number(col.min);
    const preventNegative = Number.isFinite(minValue) && minValue >= 0;

    return (
      <div className="form-group">
        <label className="form-label" htmlFor={field}>
          {col.label}
        </label>
        <input
          id={field}
          name={field}
          type="number"
          className="form-input"
          value={value ?? ""}
          onChange={(event) => {
            const rawValue = event.target.value;

            if (rawValue === "") {
              onChange(field, "");
              return;
            }

            const numericValue = Number(rawValue);
            if (Number.isFinite(numericValue)) onChange(field, numericValue);
          }}
          onKeyDown={(event) => {
            if (["e", "E", "+"].includes(event.key)) event.preventDefault();
            if (preventNegative && event.key === "-") event.preventDefault();
          }}
          min={col.min ?? undefined}
          max={col.max ?? undefined}
          step={col.step ?? 1}
          placeholder={col.placeholder || ""}
          required={col.required === true}
        />
      </div>
    );
  }

  if (col.type === "WorkpermitAddition" && Array.isArray(col.subFields)) {
    return (
      <WorkpermitAdditionField
        col={col}
        formData={formData}
        onChange={onChange}
        dropdownOptions={dropdownOptions}
      />
    );
  }

  return (
    <div className="form-group">
      <label className="form-label" htmlFor={field}>
        {col.label}
      </label>

      {col.type === "textarea" ? (
        <textarea
          id={field}
          name={field}
          className="form-input"
          value={value ?? ""}
          onChange={(event) => onChange(field, event.target.value)}
          rows={col.rows || 3}
          placeholder={col.placeholder || ""}
          required={col.required === true}
        />
      ) : (
        <input
          id={field}
          name={field}
          type={col.type || "text"}
          className="form-input"
          value={value ?? ""}
          onChange={(event) => onChange(field, event.target.value)}
          placeholder={col.placeholder || ""}
          required={col.required === true}
        />
      )}
    </div>
  );
};

export default memo(FieldRenderer);
